// Timezone utility for Sri Lanka (Asia/Colombo)
import { format } from 'date-fns'
import { toZonedTime, formatInTimeZone } from 'date-fns-tz'

const SRI_LANKA_TIMEZONE = 'Asia/Colombo'

/**
 * Get current date/time in Sri Lanka timezone
 * @returns {Date} Date object adjusted to Sri Lanka timezone
 */
export const getSriLankaTime = () => {
  return toZonedTime(new Date(), SRI_LANKA_TIMEZONE)
}

/**
 * Format a date in Sri Lanka timezone
 * @param {Date|string|number} date - Date to format
 * @param {string} formatString - Format string (date-fns format)
 * @returns {string} Formatted date string
 */
export const formatSriLankaDate = (date, formatString = 'yyyy-MM-dd HH:mm:ss') => {
  return formatInTimeZone(date, SRI_LANKA_TIMEZONE, formatString)
}

/**
 * Format time for display (e.g., "02:30 PM")
 * @param {Date|string|number} date - Date to format
 * @returns {string} Formatted time string
 */
export const formatSriLankaTime = (date) => {
  return formatInTimeZone(date, SRI_LANKA_TIMEZONE, 'hh:mm a')
}

/**
 * Get start of day in Sri Lanka timezone
 * @param {Date|string|number} date - Optional date (defaults to today)
 * @returns {Date} Start of day in Sri Lanka timezone
 */
export const getSriLankaStartOfDay = (date = new Date()) => {
  const sriLankaDate = toZonedTime(date, SRI_LANKA_TIMEZONE)
  sriLankaDate.setHours(0, 0, 0, 0)
  return sriLankaDate
}

/**
 * Get end of day in Sri Lanka timezone
 * @param {Date|string|number} date - Optional date (defaults to today)
 * @returns {Date} End of day in Sri Lanka timezone
 */
export const getSriLankaEndOfDay = (date = new Date()) => {
  const sriLankaDate = toZonedTime(date, SRI_LANKA_TIMEZONE)
  sriLankaDate.setHours(23, 59, 59, 999)
  return sriLankaDate
}

/**
 * Convert any date to Sri Lanka timezone
 * @param {Date|string|number} date - Date to convert
 * @returns {Date} Date in Sri Lanka timezone
 */
export const toSriLankaTime = (date) => {
  return toZonedTime(date, SRI_LANKA_TIMEZONE)
}

/**
 * Parse database date string (removes 'Z' if present as DB stores Sri Lanka time with Z suffix)
 * @param {string} dateString - Date string from database
 * @returns {Date} Parsed date object
 */
export const parseDBDate = (dateString) => {
  if (!dateString) return new Date()
  // DB stores times with 'Z' suffix but they're actually Sri Lanka time
  // Remove the 'Z' to treat as local time
  const cleanedDate = dateString.replace('Z', '')
  return new Date(cleanedDate)
}

/**
 * Format date for display in dashboard header
 * @returns {string} Formatted date (e.g., "Thursday, November 6, 2025")
 */
export const getCurrentDateFormatted = () => {
  const sriLankaTime = getSriLankaTime()
  return format(sriLankaTime, 'EEEE, MMMM d, yyyy')
}

/**
 * Get date range for filtering
 * @param {string} range - 'today', 'week', or 'month'
 * @returns {Object} Object with fromDate and toDate
 */
export const getDateRange = (range = 'month') => {
  const now = getSriLankaTime()
  const toDate = new Date(now)
  let fromDate = new Date(now)

  if (range === 'today') {
    fromDate = getSriLankaStartOfDay(now)
  } else if (range === 'week') {
    fromDate.setDate(fromDate.getDate() - 7)
  } else {
    // month
    fromDate.setDate(fromDate.getDate() - 30)
  }

  return { fromDate, toDate }
}

export default {
  getSriLankaTime,
  formatSriLankaDate,
  formatSriLankaTime,
  getSriLankaStartOfDay,
  getSriLankaEndOfDay,
  toSriLankaTime,
  getCurrentDateFormatted,
  getDateRange,
  parseDBDate,
  SRI_LANKA_TIMEZONE
}
